import { createSelector, createSlice } from "@reduxjs/toolkit";

import { getLeague, getTeams } from "./clubhouseActions";

import { authActions } from "../auth/authSlice";

import { RootState } from "../../store";
import { ILeague, ITeam } from "../../types/api";
import { IStateDefaults } from "../../types/shared";

export interface ClubhouseState extends IStateDefaults {
    teams: ITeam[];
    league?: ILeague;
    selectedTeam?: ITeam["id"];
    selectedLeagueTeam?: ITeam["id"];
}

const initialState: ClubhouseState = {
    teams: [],
    league: undefined,
    selectedTeam: undefined,
    selectedLeagueTeam: undefined,
    loading: false,
    success: false,
};

const clubhouseSlice = createSlice({
    name: "clubhouse",
    initialState,
    reducers: {
        setSelectedTeam: (state, action) => {
            state.selectedTeam = action.payload;
        },
        setSelectedLeagueTeam: (state, action) => {
            state.selectedLeagueTeam = action.payload;
        },
        setLeague: (state, action) => {
            state.league = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.teams = [];
                state.selectedTeam = undefined;
            })
            .addCase(getTeams.pending, state => {
                state.loading = true;
            })
            .addCase(getTeams.fulfilled, (state, { payload }) => {
                state.teams = payload.teams;
                if (payload.teams.length === 1) {
                    state.selectedTeam = payload.teams[0].id;
                }
                state.loading = false;
            })
            .addCase(getTeams.rejected, state => {
                state.loading = false;
            })
            .addCase(getLeague.pending, state => {
                state.loading = true;
            })
            .addCase(getLeague.fulfilled, (state, { payload }) => {
                payload.league.teams?.sort(
                    (a, b) =>
                        (a.name && b.name && a.name.localeCompare(b.name)) || 0,
                );
                state.league = payload.league;
                state.loading = false;
            })
            .addCase(getLeague.rejected, state => {
                state.loading = false;
            });
    },
});

export const clubhouseActions = {
    ...clubhouseSlice.actions,
    getTeams: getTeams,
    getLeague: getLeague,
};

export default clubhouseSlice.reducer;

export const selectSelectedTeam = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => {
        const selectedTeam = clubhouse.selectedTeam;
        if (selectedTeam) {
            const team = clubhouse.teams.find(team => team.id === selectedTeam);
            return team;
        }
        return undefined;
    },
);

export const selectSelectedLeagueTeam = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => {
        const selectedTeam = clubhouse.selectedLeagueTeam;
        if (selectedTeam) {
            const team = clubhouse.league?.teams?.find(
                team => team.id === selectedTeam,
            );

            return team;
        }
        return undefined;
    },
);

export const selectClubhouseTeams = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => clubhouse.teams,
);

export const selectLeague = createSelector(
    [(state: RootState) => state.clubhouse],
    clubhouse => clubhouse.league,
);
